import { Popover } from 'radix-ui';
import { type KeyboardEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { cn } from '@/shared/lib';
import { Input } from './Input';

export interface SearchComboboxItem {
  id: string;
  label: string;
  /** Краткие данные справа (11 ink-500). */
  meta?: ReactNode;
  disabled?: boolean;
}

export interface SearchComboboxProps<TItem extends SearchComboboxItem> {
  id?: string;
  /** Подпись выбранного значения — в поле, пока не начат новый поиск. */
  label: string;
  /** Варианты по текущему запросу (их загружает раздел — `onQueryChange`). */
  items: readonly TItem[];
  /** Текст под вариантами: «Ищем…», «Ничего не найдено», ошибка. */
  status?: string | null;
  /** Текст поиска (с паузой 300 мс) и открыт ли список — раздел запрашивает варианты. */
  onQueryChange: (query: string, open: boolean) => void;
  onPick: (item: TItem) => void;
  placeholder?: string;
  listLabel: string;
  disabled?: boolean;
  invalid?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Выбор из серверного справочника поиском по вводу (README «Поля форм → combo»: значение — только из
 * списка): пауза 300 мс, стрелки и Enter, Esc — отмена ввода. Варианты ищет раздел.
 */
export function SearchCombobox<TItem extends SearchComboboxItem>({
  id,
  label,
  items,
  status,
  onQueryChange,
  onPick,
  placeholder,
  listLabel,
  disabled = false,
  invalid = false,
  ...aria
}: SearchComboboxProps<TItem>) {
  const listId = useId();
  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const text = query ?? label;
  const active = items[Math.min(activeIndex, items.length - 1)];

  const trimmed = (query ?? '').trim();
  useEffect(() => {
    const timer = window.setTimeout(() => onQueryChange(trimmed, open), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed, open, onQueryChange]);

  function pick(item: TItem) {
    if (item.disabled) return;
    onPick(item);
    setQuery(null);
    setOpen(false);
    setActiveIndex(0);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setQuery(null);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && active) pick(active);
      return;
    }
    if (!open || items.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <div className="relative min-w-0 flex-1">
          <Input
            id={id}
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listId}
            aria-invalid={invalid || undefined}
            {...aria}
            value={text}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              setOpen(false);
              setQuery(null);
            }}
          />
        </div>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={2}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="z-60 max-h-210 w-(--radix-popover-trigger-width) overflow-y-auto rounded-16 border border-line-strong bg-paper-pure shadow-dropdown animate-rise-menu"
        >
          <div id={listId} role="listbox" aria-label={listLabel}>
            {items.map((item, index) => (
              <div
                key={item.id}
                role="option"
                tabIndex={-1}
                aria-selected={item === active}
                aria-disabled={item.disabled || undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(item)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') pick(item);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'flex items-center gap-10 border-b border-line-soft px-10 py-7 last:border-b-0',
                  item.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                  item === active ? 'bg-surface-sunken' : undefined,
                )}
              >
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink-900">
                  {item.label}
                </span>
                {item.meta ? (
                  <span className="text-[11px] whitespace-nowrap text-ink-500">{item.meta}</span>
                ) : null}
              </div>
            ))}
          </div>
          {status ? (
            <p className="m-0 px-10 py-7 text-[12px] leading-[18px] text-ink-500">{status}</p>
          ) : null}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
